import type { JSX } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, RefreshCw } from "lucide-react";
import { api, type StockPerformanceRow } from "../api.js";
import { PageHeader } from "../components/PageHeader.js";
import { PageActions } from "../components/PageActions.js";
import { Money } from "../components/Money.js";
import { cn } from "../lib/utils.js";
import { csvMoney, downloadCsv } from "../lib/csv.js";
import { DataTable, uniq, type Column } from "../components/data-table.js";

// Percentages are stored as ratios (0.1234 = 12.34%).
const pct = (s: string | null): string => (s == null ? "—" : `${(Number(s) * 100).toFixed(2)}%`);
const pctNum = (s: string | null): number => (s == null ? 0 : Number(s));

export function StocksPerformance(): JSX.Element {
  const qc = useQueryClient();
  const perfQ = useQuery({ queryKey: ["stock-performance"], queryFn: () => api.stockPerformance() });
  const rows = perfQ.data?.items ?? [];

  const recompute = useMutation({
    mutationFn: () => api.recomputeStocks(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stock-performance"] }),
  });

  const exportCsv = (): void => {
    downloadCsv(
      "stock-performance.csv",
      ["Symbol", "Name", "Qty", "MarketValueEUR", "TotalBenefitEUR", "Return%", "XIRR%", "DividendsNetEUR", "FXEffectEUR"],
      rows.map((r) => [
        r.symbol, r.name ?? "", r.quantity, csvMoney(r.market_value_minor), csvMoney(r.total_benefit_minor),
        pct(r.total_return_pct), pct(r.xirr_pct), csvMoney(r.dividends_net_minor), csvMoney(r.fx_effect_minor),
      ]),
    );
  };

  const moneyCell = (v: number | null, colored = false): JSX.Element =>
    v == null ? <>—</> : (
      <span className={cn(colored && (v < 0 ? "text-bad" : "text-good"))}>
        <Money amountMinor={v} native="EUR" />
      </span>
    );

  const columns: Column<StockPerformanceRow>[] = [
    {
      key: "symbol",
      label: "Symbol",
      sort: (a, b) => a.symbol.localeCompare(b.symbol),
      filter: { kind: "search", accessor: (r) => `${r.symbol} ${r.name ?? ""}`, placeholder: "Symbol / name…" },
      cell: (r) => (
        <Link to={`/stocks/s/${r.security_id}`} className="text-brand hover:underline">
          <div className="font-medium">{r.symbol}</div>
          {r.name && <div className="text-muted-foreground text-xs">{r.name}</div>}
        </Link>
      ),
    },
    { key: "class", label: "Class", sort: (a, b) => a.asset_class.localeCompare(b.asset_class), filter: { kind: "select", accessor: (r) => r.asset_class, options: (rs) => uniq(rs.map((r) => r.asset_class)) }, cell: (r) => r.asset_class },
    { key: "value", label: "Market value", align: "right", sort: (a, b) => (a.market_value_minor ?? 0) - (b.market_value_minor ?? 0), cell: (r) => moneyCell(r.market_value_minor) },
    { key: "benefit", label: "Total benefit", align: "right", sort: (a, b) => (a.total_benefit_minor ?? 0) - (b.total_benefit_minor ?? 0), cell: (r) => <span className="font-medium">{moneyCell(r.total_benefit_minor, true)}</span> },
    { key: "return", label: "Return %", align: "right", sort: (a, b) => pctNum(a.total_return_pct) - pctNum(b.total_return_pct), cell: (r) => <span className={cn(pctNum(r.total_return_pct) < 0 ? "text-bad" : "text-good")}>{pct(r.total_return_pct)}</span> },
    { key: "xirr", label: "XIRR", align: "right", sort: (a, b) => pctNum(a.xirr_pct) - pctNum(b.xirr_pct), cell: (r) => <span className={cn(pctNum(r.xirr_pct) < 0 ? "text-bad" : "text-good")}>{pct(r.xirr_pct)}</span> },
    { key: "div", label: "Dividends", align: "right", sort: (a, b) => (a.dividends_net_minor ?? 0) - (b.dividends_net_minor ?? 0), cell: (r) => moneyCell(r.dividends_net_minor) },
    { key: "fx", label: "FX effect", align: "right", sort: (a, b) => (a.fx_effect_minor ?? 0) - (b.fx_effect_minor ?? 0), cell: (r) => moneyCell(r.fx_effect_minor, true) },
  ];

  return (
    <>
      <PageHeader title="Performance">
        <PageActions
          updatedAt={rows[0]?.as_of ?? null}
          actions={[
            { label: recompute.isPending ? "Recomputing…" : "Recompute", icon: RefreshCw, onClick: () => { recompute.mutate(); } },
            { label: "Export CSV", icon: Download, onClick: exportCsv },
          ]}
        />
      </PageHeader>
      {recompute.data && recompute.data.failed > 0 && (
        <p className="text-destructive text-sm">
          Recompute: {recompute.data.failed} of {recompute.data.failed + recompute.data.securities} securities failed — the Worker log names them; the others are current.
        </p>
      )}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.security_id}
        defaultSort="benefit"
        loading={perfQ.isLoading}
        emptyText="No performance yet — run “Recompute” after a broker sync."
      />
    </>
  );
}
